import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Loader2, Newspaper, X } from 'lucide-react';

export type BeehiivSubscriberModalDetail = {
  subscription: {
    id: string;
    email: string;
    status: string;
    created: number;
    subscriptionTier: string;
    linkedUrl?: string;
  };
  tags: string[];
  customFields: Array<{ name: string; kind: string; value: unknown }>;
};

function formatBeehiivUnixCreated(created: number): string {
  if (!created) return '—';
  try {
    return new Date(created * 1000).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(created);
  }
}

function beehiivSubscriberStatusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === 'active') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';
  }
  if (s === 'pending' || s === 'validating') {
    return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200';
  }
  if (s === 'inactive' || s === 'invalid') {
    return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200';
}

function beehiivCustomFieldDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function beehiivCustomFieldValuePresent(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && !value.trim()) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

interface BeehiivSubscriberModalProps {
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
  error: string | null;
  detail: BeehiivSubscriberModalDetail | null;
}

const BeehiivSubscriberModal: React.FC<BeehiivSubscriberModalProps> = ({
  isOpen,
  onClose,
  loading,
  error,
  detail,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-6xl flex-col overflow-hidden bg-white shadow-xl dark:bg-gray-800 min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="beehiiv-subscriber-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex min-w-0 items-center gap-3">
            <div className="shrink-0 rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400">
              <Newspaper className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 id="beehiiv-subscriber-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Beehiiv subscriber
              </h2>
              <p className="truncate text-sm text-gray-500 dark:text-gray-400">Publication subscription record</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 lg:px-6">
          {loading && !detail ? (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-emerald-600" aria-hidden />
              Loading subscriber from beehiiv…
            </div>
          ) : error && !detail ? (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : detail ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-8 lg:items-start">
              <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Subscriber
                  </span>
                  <Link
                    to={`/beehiiv-subscriptions?sub=${encodeURIComponent(detail.subscription.id)}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                  >
                    Open in subscribers
                    <ExternalLink className="h-3 w-3" aria-hidden />
                  </Link>
                </div>
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/90 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Email
                    </p>
                    <p className="mt-1.5 break-all font-mono text-sm text-gray-900 dark:text-gray-100">
                      {detail.subscription.email || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Status
                    </p>
                    <div className="mt-1.5">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${beehiivSubscriberStatusClass(detail.subscription.status)}`}
                      >
                        {detail.subscription.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Tier
                    </p>
                    <p className="mt-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {detail.subscription.subscriptionTier || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Subscription date
                    </p>
                    <p className="mt-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatBeehiivUnixCreated(detail.subscription.created)}
                    </p>
                  </div>
                  {detail.subscription.linkedUrl ? (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        LinkedIn / profile URL
                      </p>
                      <a
                        href={detail.subscription.linkedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 inline-block break-all text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                      >
                        {detail.subscription.linkedUrl}
                      </a>
                    </div>
                  ) : null}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Subscription ID
                    </p>
                    <p
                      className="mt-1.5 break-all font-mono text-xs text-gray-800 dark:text-gray-200"
                      title={detail.subscription.id}
                    >
                      {detail.subscription.id}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Tags
                  </p>
                  {detail.tags.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No tags</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {detail.tags.map((t, i) => (
                        <span
                          key={`${t}-${i}`}
                          className="inline-flex rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="min-w-0 lg:border-l lg:border-gray-200 lg:pl-8 dark:lg:border-gray-700">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Custom fields
                </p>
                {detail.customFields.filter((f) => beehiivCustomFieldValuePresent(f.value)).length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No values on file.</p>
                ) : (
                  <dl className="max-h-[min(52vh,28rem)] space-y-3 overflow-y-auto pr-1">
                    {detail.customFields
                      .filter((f) => beehiivCustomFieldValuePresent(f.value))
                      .map((f, idx) => (
                        <div
                          key={`${f.name}-${idx}`}
                          className="rounded-md border border-gray-100 bg-gray-50/80 px-3 py-2 dark:border-gray-700/80 dark:bg-gray-900/50"
                        >
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{f.name}</dt>
                          <dd className="mt-1 break-words text-sm text-gray-900 dark:text-gray-100">
                            {beehiivCustomFieldDisplayValue(f.value)}
                          </dd>
                        </div>
                      ))}
                  </dl>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No data.</p>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-gray-200 bg-gray-50/90 px-5 py-3 dark:border-gray-700 dark:bg-gray-900/50 lg:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BeehiivSubscriberModal;
